import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ShieldCheck, Mail, Lock, Eye, EyeOff, Shield } from 'lucide-react';
import { Turnstile } from '../../components/Turnstile';

import SenderoLogo from '../../components/SenderoLogo';
import MaintenanceBanner from '../../components/MaintenanceBanner';
import PublicChatAssistant from '../../components/PublicChatAssistant';
import { isTurnstileEnabled } from '../../utils/runtimeConfig';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';




const AnimInput = ({ label, hint, icon, type = 'text', value, onChange, obscure }) => {
    const [focused, setFocused] = useState(false);
    const [show, setShow] = useState(!obscure);
    return (
        <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: focused ? '#0D9488' : '#6B7280', transition: 'color 0.2s', marginBottom: 8 }}>{label}</div>
            <div style={{
                background: focused ? 'rgba(13,148,136,0.03)' : '#F8FAFB',
                border: `${focused ? 1.5 : 1}px solid ${focused ? '#0D9488' : '#E8ECF0'}`,
                borderRadius: 14, display: 'flex', alignItems: 'center', padding: '0 14px',
                boxShadow: focused ? '0 4px 12px rgba(13,148,136,0.08)' : 'none',
                transition: 'all 0.2s',
            }}>
                <span style={{ marginRight: 10, opacity: focused ? 1 : 0.5, transition: 'opacity 0.2s', display: 'flex', alignItems: 'center' }}>{icon}</span>
                <input
                    type={obscure ? (show ? 'text' : 'password') : type}
                    placeholder={hint}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    style={{ border: 'none', outline: 'none', padding: '16px 0', fontSize: 15, width: '100%', background: 'transparent', color: '#1A1D21', fontFamily: 'inherit' }}
                />
                {obscure && (
                    <span onClick={() => setShow(!show)} style={{ cursor: 'pointer', opacity: 0.5, display: 'flex', alignItems: 'center' }}>
                        {show ? <Eye size={18} strokeWidth={2} /> : <EyeOff size={18} strokeWidth={2} />}
                    </span>
                )}
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(255,255,255,0.7)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
};

/* ── OTP Input: 6 individual boxes ────────────────────────────────── */
const OtpInput = ({ length = 6, value, onChange, onKeyDown }) => {
    const inputs = useRef([]);
    const digits = value.split('').concat(Array(length - value.length).fill(''));

    const handleChange = (idx, val) => {
        if (!/^\d?$/.test(val)) return;
        const newDigits = [...digits];
        newDigits[idx] = val;
        onChange(newDigits.join(''));
        if (val && idx < length - 1) inputs.current[idx + 1]?.focus();
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
            inputs.current[idx - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
        onChange(pasted);
        const focusIdx = Math.min(pasted.length, length - 1);
        setTimeout(() => inputs.current[focusIdx]?.focus(), 0);
    };

    return (
        <div
            className="otp-input-row"
            style={{
                display: 'flex',
                gap: 'clamp(6px, 2vw, 10px)',
                justifyContent: 'center',
                marginBottom: 24,
                width: '100%',
                maxWidth: '100%',
            }}
        >
            {digits.slice(0, length).map((d, i) => (
                <input
                    key={i}
                    ref={el => inputs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => handleChange(i, e.target.value)}
                    onKeyDown={e => { handleKeyDown(i, e); onKeyDown?.(e); }}
                    onPaste={i === 0 ? handlePaste : undefined}
                    aria-label={`Digit ${i + 1}`}
                    style={{
                        flex: '1 1 0',
                        minWidth: 0,
                        maxWidth: 56,
                        aspectRatio: '1 / 1.15',
                        padding: 0,
                        borderRadius: 12,
                        border: `1.5px solid ${d ? '#0D9488' : '#E8ECF0'}`,
                        background: d ? 'rgba(13,148,136,0.03)' : '#F8FAFB',
                        fontSize: 'clamp(18px, 5vw, 22px)',
                        fontWeight: 700,
                        textAlign: 'center',
                        color: '#1A1D21',
                        fontFamily: 'inherit',
                        outline: 'none',
                        transition: 'all 0.2s',
                        boxShadow: d ? '0 2px 8px rgba(13,148,136,0.08)' : 'none',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#0D9488'; e.target.style.boxShadow = '0 4px 12px rgba(13,148,136,0.08)'; }}
                    onBlur={e => { if (!d) { e.target.style.borderColor = '#E8ECF0'; e.target.style.boxShadow = 'none'; } }}
                />
            ))}
        </div>
    );
};

/* ── Login Page ────────────────────────────────────────────────────── */
const LoginPage = () => {
    
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const turnstileRef = useRef(null);//
    const toast = useToast();
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [mfaScreenVisible, setMfaScreenVisible] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [turnstileToken, setTurnstileToken] = useState(null);
    const { login, mfaPending, mfaEmail, verifyMfa, resendMfa, cancelMfa } = useAuth();
    const navigate = useNavigate();

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);



    useEffect(() => {       

        if (
            turnstileToken &&
            typeof turnstileToken === 'string' &&
            turnstileToken.trim() !== ''
        ) {

            setPageLoading(false);

        } else {
            console.log("Truenstile token not generated");
            setPageLoading(true);
        }

    }, [turnstileToken]);

    

    const handleSubmit = async () => {

        

        setError('');
        if (!username || !password) { setError('Please enter both username and password.'); return; }

        if (turnstileToken == null || turnstileToken == "") {
            location.reload();
            return;
        }

        setLoading(true);

        try {

            const result = await login(username, password, turnstileToken);
                    


            //console.log("LOGI RESPONSE : "+JSON.stringify(result.data.Success));

            //if (!result) {
            if (!result.data.Success) {

                //setError("Invalid username or password.");
                setError(result?.data?.Message);
                
                // clear old token
                setTurnstileToken(null);

                // generate fresh token
                turnstileRef.current?.reset();

            }
            //else if (result && result != 'mfa_required') {
            else if (result.data.Success && !result.data.TwoFactorRequired) {

                toast.success('User login successfully', { title: 'Login success' });
                navigate('/');
            }
            //else if (result === 'mfa_required') {
            else if (result.data.TwoFactorRequired) {
                setResendCooldown(30);
                setMfaScreenVisible(true);
            }
            else {
                setError('Invalid username or password.');

                // clear old token
                setTurnstileToken(null);

                // generate fresh token
                turnstileRef.current?.reset();
                
             
            }

            
        } catch(err) {
            console.log("ERROR :"+err);
            setError('Unable to connect to server. Please try again.');
            setLoading(false);
            // clear old token
            setTurnstileToken(null);

            // generate fresh token
            turnstileRef.current?.reset();
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {

        setError('');
        if (otp.length !== 6) { setError('Please enter the 6-digit code.'); return; }
        setLoading(true);
        try {
            const success = await verifyMfa(otp);           
          //  setMfaPending(false);

            if (success) {
                
                navigate('/');
               // setMfaScreenVisible(false);
            }
            else {
                setError('Invalid verification code. Please try again.');
            }
            
        } catch {
            setError('Verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        const success = await resendMfa();
        if (success) {
            setResendCooldown(30);
            setError('');
        } else {
            setError('Failed to resend code. Please try again.');
        }
    };

    const handleBackToLogin = () => {
        cancelMfa();
        setOtp('');
        setError('');
        setMfaScreenVisible(false);
    };

    const maskEmail = (email) => {
        if (!email) return '***@***.com';
        const [local, domain] = email.split('@');
        const masked = local.slice(0, 2) + '***';
        return `${masked}@${domain}`;
    };

    /* ── MFA Verification Screen ──────────────────────────────────────── */
    if (mfaScreenVisible) {
        return (
            <div className="auth-layout auth-layout--mfa">
                {/* Keep the global maintenance notice visible through the MFA step */}
                <MaintenanceBanner fixed />
                <div className="auth-mfa-header" style={{
                    background: 'linear-gradient(135deg, #0F766E, #14B8A6, #2DD4BF)',
                    padding: 'clamp(24px, 6vw, 40px) 20px clamp(20px, 4vw, 30px)',
                    borderRadius: '0 0 clamp(24px, 6vw, 36px) clamp(24px, 6vw, 36px)',
                    textAlign: 'center',
                    width: '100%',
                    maxWidth: 440,
                    boxShadow: '0 10px 30px rgba(13,148,136,0.3)',
                    marginBottom: -20,
                    zIndex: 2,
                    position: 'relative',
                    boxSizing: 'border-box',
                }}>
                    <div style={{
                        width: 'clamp(52px, 14vw, 64px)',
                        height: 'clamp(52px, 14vw, 64px)',
                        borderRadius: 20, background: '#fff',
                        margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                    }}>
                        <ShieldCheck size={32} color="#0D9488" strokeWidth={2} />
                    </div>
                    <div style={{
                        fontSize: 'clamp(20px, 5.5vw, 24px)',
                        fontWeight: 800, color: '#fff',
                        marginTop: 14, letterSpacing: -0.5, lineHeight: 1.2,
                    }}>
                        Verify Your Identity
                    </div>
                    <div style={{
                        fontSize: 'clamp(12px, 3.5vw, 14px)',
                        color: 'rgba(255,255,255,0.85)', marginTop: 4,
                    }}>
                        Multi-factor authentication
                    </div>
                </div>

                <div className="auth-card auth-card--mfa" style={{ paddingTop: 36, position: 'relative', zIndex: 1 }}>
                    {error && <div className="error-text" style={{ marginBottom: 16 }}>{error}</div>}

                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6 }}>
                            We've sent a 6-digit verification code to
                        </div>
                        <div style={{
                            fontSize: 'clamp(13px, 4vw, 15px)',
                            fontWeight: 700, color: '#1A1D21', marginTop: 4,
                            wordBreak: 'break-all',
                        }}>
                            {maskEmail(mfaEmail)}
                        </div>
                    </div>

                    <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', textAlign: 'center', marginBottom: 12 }}>
                        Enter verification code
                    </div>

                    <OtpInput length={6} value={otp} onChange={setOtp}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && otp.length === 6 && !loading) {
                                handleVerifyOtp();
                            }
                        }}
                    />

                    {/* Verify Button */}
                    <div
                        onClick={handleVerifyOtp}
                        style={{
                            width: '100%', height: 52, borderRadius: 14, cursor: 'pointer',
                            background: otp.length === 6 ? 'linear-gradient(135deg, #0D9488, #0F766E)' : '#E5E7EB',
                            boxShadow: otp.length === 6 ? '0 6px 16px rgba(13,148,136,0.3)' : 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s', userSelect: 'none',
                        }}
                        onMouseDown={e => { if (otp.length === 6) e.currentTarget.style.transform = 'scale(0.97)'; }}
                        onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                        onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                        {loading ? (
                            <div style={{ width: 22, height: 22, border: '2.5px solid rgba(255,255,255,0.4)', borderTop: '2.5px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        ) : (
                            <span style={{ fontSize: 15, fontWeight: 700, color: otp.length === 6 ? '#fff' : '#9CA3AF', letterSpacing: 0.3 }}>
                                Verify & Sign In
                            </span>
                        )}
                    </div>

                    {/* Resend + Back */}
                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>
                            Didn't receive the code?{' '}
                            {resendCooldown > 0 ? (
                                <span style={{ color: '#9CA3AF' }}>Resend in {resendCooldown}s</span>
                            ) : (
                                <span
                                    onClick={handleResend}
                                    style={{ color: '#0D9488', fontWeight: 700, cursor: 'pointer' }}
                                >
                                    Resend Code
                                </span>
                            )}
                        </div>
                        <div
                            onClick={handleBackToLogin}
                            style={{ fontSize: 13, color: '#6B7280', cursor: 'pointer', marginTop: 12 }}
                        >
                            <span style={{ color: '#0D9488', fontWeight: 600 }}>
                                ← Back to Login
                            </span>
                        </div>
                    </div>

                    {/* Demo hint */}
                    {/*<div style={{*/}
                    {/*    marginTop: 24, padding: '10px 14px', borderRadius: 10,*/}
                    {/*    background: 'rgba(13,148,136,0.06)', border: '1px solid rgba(13,148,136,0.15)',*/}
                    {/*    fontSize: 12, color: '#0F766E', textAlign: 'center',*/}
                    {/*}}>*/}
                    {/*    Demo mode: Enter <strong>123456</strong> to verify*/}
                    {/*</div>*/}
                </div>

                <div className="auth-footer-links">
                    <a href="#terms">Terms of Service</a>
                    <a href="#privacy">Privacy Policy</a>
                    <a href="#contact">Contact</a>
                </div>
            </div>
        );
    }

    /* ── Normal Login Screen ──────────────────────────────────────────── */
    return (
        <div className="auth-layout">
            {/* Global maintenance notice (fixed so it floats above the auth background) */}
            <MaintenanceBanner fixed />

            {/* Pre-login public help assistant (super-admin gated, PHI-free). Sending is
                bot-gated: enabled once Turnstile has verified this browser. */}
            <PublicChatAssistant ready={!isTurnstileEnabled() || !!turnstileToken} />

            {pageLoading && (
                <div data-cy="page-loader-login" style={styles.overlay} name="PageLoaderLogin" id="PageLoaderLogin">
                    <LoadingSpinner message="Please wait..." fullPage="true" />
                </div>
            )}
            <div style={{
                background: 'linear-gradient(135deg, #0F766E, #14B8A6, #2DD4BF)',
                padding: '50px 0 36px', borderRadius: '0 0 36px 36px',
                textAlign: 'center', width: '100%', maxWidth: 440,
                boxShadow: '0 10px 30px rgba(13,148,136,0.3)',
                marginBottom: -20, zIndex: 2, position: 'relative'
            }}>
                <div style={{ width: 78, height: 78, borderRadius: 22, background: '#fff', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 20px rgba(0,0,0,0.1)', padding: 8 }}>
                    <SenderoLogo variant="mark" height={56} />
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#fff', marginTop: 16, letterSpacing: -0.5 }}>Welcome Back</div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>Sign in to your provider portal</div>
            </div>

            <div className="auth-card" style={{ paddingTop: 40, position: 'relative', zIndex: 1 }}>
                {error && <div className="error-text" style={{ marginBottom: 16 }}>{error}</div>}

                <AnimInput label="Username" hint="Enter your username" icon={<Mail size={18} color="#6B7280" strokeWidth={2} />} value={username} onChange={setUsername} />
                <AnimInput label="Password" hint="Enter your password" icon={<Lock size={18} color="#6B7280" strokeWidth={2} />} value={password} onChange={setPassword} obscure />

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: -12, marginBottom: 20 }}>
                    <Link to="/forgot-username" style={{ fontSize: 13, fontWeight: 600, color: '#0D9488' }}>Forgot Username?</Link>
                    <Link to="/forgot-password" style={{ fontSize: 13, fontWeight: 600, color: '#0D9488' }}>Forgot Password?</Link>
                </div>

                <div style={{ marginBottom: 28 }} />

                <div
                    data-cy="signin-btn"
                    onClick={handleSubmit}
                    style={{
                        width: '100%', height: 56, borderRadius: 16, cursor: 'pointer',
                        background: 'linear-gradient(135deg, #0D9488, #0F766E)',
                        boxShadow: '0 6px 16px rgba(13,148,136,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'transform 0.1s', userSelect: 'none',
                    }}
                    onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.97)')}
                    onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                    onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                >
                    {loading ? (
                        <div style={{ width: 22, height: 22, border: '2.5px solid rgba(255,255,255,0.4)', borderTop: '2.5px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    ) : (
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>Sign In</span>
                    )}
                </div>

                <div style={{ textAlign: 'center', marginTop: 28, fontSize: 14, color: '#6B7280' }}>
                    Don't have an account? <Link to="/register" style={{ color: '#0D9488', fontWeight: 700 }}>Create Account</Link>
                </div>
            </div>

            <div className="auth-footer-links">
                <a href="/terms">Terms of Service</a>
                <a href="/privacy">Privacy Policy</a>
                <a href="/contact">Contact</a>
            </div>

            

            {/* Cloudflare Turnstile - invisible bot protection */}
            {/*<Turnstile onVerify={setTurnstileToken} />*/}


            <Turnstile onVerify={setTurnstileToken} ref={turnstileRef} />




        </div>
    );
};

export default LoginPage;
